import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export interface PollingOptions {
  interval?: number
  stopAfterFailedAttempts?: number
  onCleanUp?: () => Promise<void>
  autoStart?: boolean
}

export interface PollingControls {
  started: boolean
  start: () => void
  stop: () => void
}

export const usePollingEffect = (
  asyncCallback: () => Promise<void>,
  dependencies: unknown[] = [],
  { interval = 10_000, stopAfterFailedAttempts = 3, onCleanUp = undefined, autoStart = false }: PollingOptions = {},
): PollingControls => {
  const [started, setStarted] = useState(autoStart)

  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const callbackRef = useRef(asyncCallback)

  useEffect(() => {
    callbackRef.current = asyncCallback
  }, [asyncCallback])

  useEffect(() => {
    if (!started) {
      return
    }

    let _stopped = false
    let failedAttempts = 0
    const pollingCallback = async () => {
      try {
        await callbackRef.current()
      } catch (e) {
        console.error(e)
        failedAttempts = failedAttempts + 1
        if (failedAttempts >= stopAfterFailedAttempts) {
          _stopped = true
          setStarted(false)
        }
      } finally {
        if (!_stopped) {
          timeoutIdRef.current = setTimeout(pollingCallback, interval)
        }
      }
    }

    pollingCallback().catch(console.error)

    return () => {
      _stopped = true
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current)
      }

      if (onCleanUp) {
        onCleanUp()
      }
    }
  }, [...dependencies, interval, started, onCleanUp, stopAfterFailedAttempts])

  const start = useCallback(() => setStarted(true), [])
  const stop = useCallback(() => setStarted(false), [])

  return useMemo(() => {
    return {
      started,
      start,
      stop,
    } as PollingControls
  }, [started, start, stop])
}
